import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Github, Server, ExternalLink, ChevronLeft, ChevronRight, Download, Play } from 'lucide-react';
import PageLayout from '../layout/PageLayout';

const CommercialBreakerPage: React.FC = () => {
  const [currentScreenshot, setCurrentScreenshot] = useState(0);

  const screenshots = [
    {
      src: '/images/commercial-breaker/page1.png',
      title: 'Step 1 - Choose Platform',
      description: 'Welcome to the Absolution. Pick your platform: DizqueTV, Tunarr, or ComBreakDirect.'
    },
    {
      src: '/images/commercial-breaker/page2a.png',
      title: 'Step 2a - Login to Plex (DizqueTV/Tunarr)',
      description: 'Secure Docking: Login with Plex authentication and select your server. (Skip this if using ComBreakDirect)'
    },
    {
      src: '/images/commercial-breaker/page2b.png',
      title: 'Step 2b - Manual Setup (Optional)',
      description: 'A Little Detour: Enter Plex details manually - URL, token, and library names.'
    },
    {
      src: '/images/commercial-breaker/page2.png',
      title: 'Step 3 - Content Prep',
      description: 'Prepare your content and configure your anime lineup for processing.'
    },
    {
      src: '/images/commercial-breaker/page3.png',
      title: 'Step 4 - Commercial Breaker',
      description: 'Configure cutless mode and select files to process. Toonami Will Be Right Back.'
    },
    {
      src: '/images/commercial-breaker/page4.png',
      title: 'Step 5 - Create Your Toonami Channel',
      description: 'Create your channel and prepare your cut anime for the perfect Toonami lineup.'
    },
    {
      src: '/images/commercial-breaker/page5.png',
      title: 'Step 6 - Channel Creation',
      description: 'Create your Toonami channel with custom lineup and commercial breaks.'
    },
    {
      src: '/images/commercial-breaker/page6.png',
      title: 'Step 7 - ComBreakDirect Info',
      description: 'ComBreakDirect users: All the information you need to connect and start streaming.'
    },
  ];

  const features = [
    {
      title: 'Three Interfaces',
      items: [
        'TOM (Desktop GUI) - Tkinter-based interface',
        'Absolution (Web UI) - Remi-powered web interface',
        'Clydes (CLI) - Command-line for the terminal enthusiasts'
      ]
    },
    {
      title: 'Platform Support',
      items: [
        'DizqueTV integration',
        'Tunarr integration',
        'ComBreakDirect - Our own streaming server',
        'Plex & Jellyfin compatible'
      ]
    },
    {
      title: 'Dynamic Workflow',
      items: [
        'DizqueTV/Tunarr: Plex login (Step 2a) → Optional manual setup (Step 2b) → Content prep',
        'ComBreakDirect: Skip Plex entirely → Jump straight to content prep',
        'Workflow adapts on-the-fly based on your platform choice',
        'Only shows the steps you need'
      ]
    },
    {
      title: 'Cutting Modes',
      items: [
        'Traditional Mode - Physical file cutting',
        'Cutless Mode - Virtual timestamps (saves disk space)',
        'Automatic commercial break detection',
        'Chapter marker support'
      ]
    },
    {
      title: 'Toonami Features',
      items: [
        'Authentic bump integration',
        'Multi-lineup support',
        'Transitional bumps between shows',
        'Special bumps (music videos, promos)',
        'Custom network support (not just Toonami!)'
      ]
    }
  ];

  const techStack = [
    { name: 'Python', description: 'Core application logic' },
    { name: 'Flask', description: 'Web interface & API' },
    { name: 'Tkinter', description: 'Desktop GUI (TOM)' },
    { name: 'FFmpeg', description: 'Video processing' },
    { name: 'SQLite', description: 'Database & lineup management' },
  ];

  const nextScreenshot = () => {
    setCurrentScreenshot((prev) => (prev + 1) % screenshots.length);
  };

  const prevScreenshot = () => {
    setCurrentScreenshot((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-theme font-bold mb-6 text-gradient">
              CommercialBreaker
            </h1>
            <p className="text-2xl md:text-3xl mb-4 font-semibold italic" style={{ color: 'var(--color-text-secondary)' }}>
              Because your anime deserves commercial breaks
            </p>
            <p className="text-lg md:text-xl max-w-3xl mx-auto" style={{ color: 'var(--color-text)' }}>
              Yeah, we're those idiots who put commercials back into commercial-free programming.
              <br />
              What are you gonna do about it?
            </p>
          </motion.div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <a
              href="https://github.com/theweebcoders/CommercialBreaker"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105 shadow-md"
              style={{
                background: 'var(--gradient-primary)',
                color: 'var(--color-bg)'
              }}
            >
              <Github size={20} />
              <span>View on GitHub</span>
              <ExternalLink size={16} />
            </a>
            <a
              href="https://hub.docker.com/r/tim000x3/commercial-breaker"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold border-2 transition-all hover:scale-105"
              style={{
                borderColor: 'var(--color-primary)',
                color: 'var(--color-primary)',
                backgroundColor: 'var(--color-primary)' + '10'
              }}
            >
              <Server size={20} />
              <span>Docker Hub</span>
              <ExternalLink size={16} />
            </a>
            <a
              href="https://github.com/theweebcoders/CommercialBreaker#how-to-install"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold border-2 transition-all hover:scale-105"
              style={{
                borderColor: 'var(--color-primary)',
                color: 'var(--color-primary)',
                backgroundColor: 'var(--color-primary)' + '10'
              }}
            >
              <Download size={20} />
              <span>Installation Guide</span>
            </a>
          </div>
        </div>
      </section>

      {/* Screenshot Carousel */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-theme font-bold mb-8 text-center text-gradient">
            Interface Screenshots
          </h2>

          <div className="relative">
            {/* Main Screenshot Display */}
            <div
              className="rounded-xl overflow-hidden shadow-2xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                borderWidth: '2px',
                borderStyle: 'solid'
              }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentScreenshot}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={screenshots[currentScreenshot].src}
                    alt={screenshots[currentScreenshot].title}
                    className="w-full h-auto"
                  />
                </motion.div>
              </AnimatePresence>

              {/* Navigation Arrows */}
              <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between p-4 pointer-events-none">
                <button
                  onClick={prevScreenshot}
                  className="pointer-events-auto p-3 rounded-full backdrop-blur-sm transition-all hover:scale-110"
                  style={{
                    backgroundColor: 'var(--color-bg)' + 'CC',
                    color: 'var(--color-primary)'
                  }}
                  aria-label="Previous screenshot"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextScreenshot}
                  className="pointer-events-auto p-3 rounded-full backdrop-blur-sm transition-all hover:scale-110"
                  style={{
                    backgroundColor: 'var(--color-bg)' + 'CC',
                    color: 'var(--color-primary)'
                  }}
                  aria-label="Next screenshot"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            </div>

            {/* Screenshot Info */}
            <div className="mt-6 text-center">
              <h3 className="text-xl font-theme font-bold mb-2" style={{ color: 'var(--color-primary)' }}>
                {screenshots[currentScreenshot].title}
              </h3>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                {screenshots[currentScreenshot].description}
              </p>
            </div>

            {/* Thumbnail Navigation */}
            <div className="mt-8 grid grid-cols-3 md:grid-cols-6 gap-4">
              {screenshots.map((screenshot, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentScreenshot(index)}
                  className="rounded-lg overflow-hidden transition-all hover:scale-105"
                  style={{
                    borderColor: index === currentScreenshot ? 'var(--color-primary)' : 'var(--color-border)',
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    opacity: index === currentScreenshot ? 1 : 0.6
                  }}
                >
                  <img src={screenshot.src} alt={`Thumbnail ${index + 1}`} className="w-full h-auto" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-theme font-bold mb-12 text-center text-gradient">
            Features
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  borderColor: 'var(--color-border)',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
              >
                <h3 className="text-xl font-theme font-bold mb-4" style={{ color: 'var(--color-primary)' }}>
                  {feature.title}
                </h3>
                <ul className="space-y-2">
                  {feature.items.map((item, i) => (
                    <li key={i} className="flex items-start space-x-2">
                      <span style={{ color: 'var(--color-primary)' }}>▸</span>
                      <span style={{ color: 'var(--color-text)' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-theme font-bold mb-12 text-center text-gradient">
            Tech Stack
          </h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {techStack.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="p-4 rounded-lg text-center"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  borderColor: 'var(--color-border)',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
              >
                <h3 className="font-theme font-bold text-lg mb-1" style={{ color: 'var(--color-primary)' }}>
                  {tech.name}
                </h3>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {tech.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Story */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-8 rounded-xl"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border)',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}
          >
            <h2 className="text-3xl font-theme font-bold mb-6 text-gradient">
              The Story
            </h2>
            <div className="space-y-4" style={{ color: 'var(--color-text)' }}>
              <p>
                Born from Toonami nostalgia and two years of coding in the mines, CommercialBreaker is the result of asking "what if we could recreate those long Toonami nights?"
              </p>
              <p>
                Remember those oddly comforting interruptions of commercials? The lineup bumps? TOM's voice telling you what's next? We missed that. So we built it.
              </p>
              <p>
                Started as a simple Python script. Grew into three full applications with desktop GUI, web interface, and CLI. Added Docker support. Built our own streaming server. Forked DizqueTV to add new features. Created a comprehensive wiki.
              </p>
              <p>
                Two years later, here we are. It works with Plex, Jellyfin, DizqueTV, Tunarr, or standalone with ComBreakDirect. It handles lineup generation, bump insertion, commercial break detection, and creates authentic Toonami marathons.
              </p>
              <p className="italic">
                So who's the bigger weeb - the person adding commercials back into their anime, or the developer who spent two years making it possible?
              </p>
              <p className="font-semibold">
                Yes.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Get Started CTA */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-8 rounded-xl"
            style={{
              background: 'var(--gradient-primary)',
              color: 'var(--color-bg)'
            }}
          >
            <h2 className="text-3xl font-theme font-bold mb-4">
              Ready to Build Your Own Toonami?
            </h2>
            <p className="mb-6 text-lg">
              Check out the installation guide and start your marathon today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://github.com/theweebcoders/CommercialBreaker#how-to-install"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                style={{
                  backgroundColor: 'var(--color-bg)',
                  color: 'var(--color-primary)'
                }}
              >
                <Play size={20} />
                <span>Get Started</span>
              </a>
              <a
                href="https://github.com/theweebcoders/CommercialBreaker/wiki"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold border-2 transition-all hover:scale-105"
                style={{
                  borderColor: 'var(--color-bg)',
                  color: 'var(--color-bg)',
                  backgroundColor: 'transparent'
                }}
              >
                Read the Wiki
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
};

export default CommercialBreakerPage;
